import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { IconButton } from '@mui/material';
import { AI_ASSISTANT_ITEMS } from 'constants/AI';
import { BUSINESS_TOOL_SECTIONS } from 'constants/businessTool';
import { get } from 'lodash';

import { ReactComponent as AIIcon } from 'assets/icons/AIIcon.svg';
import { ReactComponent as AIIconDefault } from 'assets/icons/AIIconDefault.svg';
import { ReactComponent as CheckIcon } from 'assets/icons/CheckIcon.svg';
import { ReactComponent as LongerIcon } from 'assets/icons/LongerIcon.svg';
import { ReactComponent as ShorterIcon } from 'assets/icons/ShorterIcon.svg';

import { useAuthenticated } from 'hooks/useAuthenticated';

import { DotsLoader } from 'components/_basic/DotsLoader';
import { DropdownMenu } from 'components/_basic/DropdownMenu';
import { IDropdownMenuItem } from 'components/_basic/DropdownMenuItem/types';
import {
  ALL_FIELD_NAMES,
  GLOBAL_FIELD_NAMES,
  GLOBAL_PRODUCTS_NAME,
} from 'components/BusinessToolForm/Sections/Global/form';
import { useGetInitialValues } from 'components/BusinessToolForm/utils/form';
import ModalContext from 'components/ModalManager/modal-context';
import { SaveProjectModal } from 'components/SaveProjectModal/SaveProjectModal';

import { checkIfNeedToGenerate } from './helper';
import { IAIAssistantContextMenuProps } from './types';

import styles from './AIAssistantContextMenu.module.scss';

export const AIAssistantContextMenu: React.FC<IAIAssistantContextMenuProps> = ({
  fieldName,
  fieldType,
  isLoading,
  onlyUpdate = false,
  onApiRequest,
  isAlreadyGenerated,
}) => {
  const [menuConfig, setMenuConfig] = useState<IDropdownMenuItem[]>([]);
  const { isAuthenticated, isAuthConfiguring } = useAuthenticated();
  const { showModal, hideModal } = useContext(ModalContext);

  const { getValues } = useFormContext();
  const { t } = useTranslation();
  const initialValues = useGetInitialValues();

  const updateMenuItems = useCallback(() => {
    const fieldNames = Array.isArray(fieldName) ? fieldName : [fieldName];

    // Initialize an object to store collective status
    let isNeedToGenerateOverall = false;

    // Check each field to determine the need for generation or improvement
    for (const name of fieldNames) {
      const fieldValue = getValues(name);
      const initialValue = get(initialValues, name);

      if (
        name === `${BUSINESS_TOOL_SECTIONS.GLOBAL}.${GLOBAL_FIELD_NAMES.COMPANY_BRIEF_DESCRIPTION}` ||
        name === `${BUSINESS_TOOL_SECTIONS.GLOBAL}.${GLOBAL_FIELD_NAMES.PROJECT_BRIEF_DESCRIPTION}` ||
        name === `${BUSINESS_TOOL_SECTIONS.EXECUTIVE_SUMMARY}.${ALL_FIELD_NAMES.COMPANY_DESCRIPTION.COMPANY_OWNERS}` ||
        name === `${BUSINESS_TOOL_SECTIONS.EXECUTIVE_SUMMARY}.${ALL_FIELD_NAMES.ORGANIZATION_AND_TEAM.MEMBERS}`
      ) {
        break;
      }

      if (name === GLOBAL_PRODUCTS_NAME) {
        const initialValuesWithoutId = initialValue.map(({ productId: _, ...value }: { productId: string }) => value);
        const fieldValuesWithoutId = fieldValue.map(({ productId: _, ...value }: { productId: string }) => value);

        if (checkIfNeedToGenerate(fieldValuesWithoutId, initialValuesWithoutId)) {
          isNeedToGenerateOverall = true;
          break;
        }
      } else {
        if (checkIfNeedToGenerate(fieldValue, initialValue)) {
          isNeedToGenerateOverall = true;
          break;
        }
      }
    }

    const saveProjectModalHandler = () => {
      showModal(SaveProjectModal, {
        onClose: hideModal,
        title: t('pleaseLogin'),
        description: t('loginForAIGeneration'),
      });
    };

    const generateClickFn =
      isAuthenticated || isAuthConfiguring
        ? () => onApiRequest(isNeedToGenerateOverall ? AI_ASSISTANT_ITEMS.GENERATE : AI_ASSISTANT_ITEMS.IMPROVE)
        : saveProjectModalHandler;

    if (isNeedToGenerateOverall && onlyUpdate) {
      return [];
    }

    return [
      {
        label: isNeedToGenerateOverall ? t('generate') : t('improve'),
        icon: <AIIconDefault className={styles.icon} />,
        onClick: generateClickFn,
      },
      ...(fieldType === 'text'
        ? [
            {
              label: t('shorter'),
              icon: <ShorterIcon className={styles.icon} />,
              onClick: () => onApiRequest(AI_ASSISTANT_ITEMS.SHORTER),
            },
            {
              label: t('longer'),
              icon: <LongerIcon className={styles.icon} />,
              onClick: () => onApiRequest(AI_ASSISTANT_ITEMS.LONGER),
            },
            {
              label: t('fixGrammar'),
              icon: <CheckIcon className={styles.icon} />,
              onClick: () => onApiRequest(AI_ASSISTANT_ITEMS.FIX_GRAMMAR),
            },
          ]
        : []),
    ];
  }, [
    fieldName,
    getValues,
    initialValues,
    isAuthConfiguring,
    isAuthenticated,
    onApiRequest,
    showModal,
    hideModal,
    t,
    fieldType,
    onlyUpdate,
  ]);

  // Initialize menu on component mount
  useEffect(() => {
    setMenuConfig(updateMenuItems());
  }, [updateMenuItems]);

  const handleMenuOpen = () => {
    setMenuConfig(updateMenuItems());
  };

  if (isLoading) {
    return (
      <div className={styles.loadingIconWrapper}>
        <DotsLoader />
      </div>
    );
  }

  const loginPromptConfig = {
    onClose: hideModal,
    title: t('pleaseLogin'),
    description: t('loginForAIGeneration'),
  };

  return (
    <DropdownMenu menuItems={menuConfig}>
      {({ handleMenuOpen: internalHandleMenuOpen }) => (
        <IconButton
          disableRipple
          disabled={menuConfig.length === 0}
          className={styles.iconButton}
          onClick={(e) => {
            if (!isAuthenticated && !isAuthConfiguring) {
              showModal(SaveProjectModal, loginPromptConfig);
              return;
            } else if (isAlreadyGenerated) {
              return;
            }

            handleMenuOpen();
            internalHandleMenuOpen(e);
          }}
        >
          <AIIcon />
        </IconButton>
      )}
    </DropdownMenu>
  );
};
